import type { PuckTreeNode } from "@/lib/cms-puck/puck-tree";

export const BLOCK_CLIPBOARD_KIND = "inventozen-puck-block" as const;
export const BLOCK_CLIPBOARD_VERSION = 1 as const;

export type BlockClipboardPayload = {
  version: typeof BLOCK_CLIPBOARD_VERSION;
  kind: typeof BLOCK_CLIPBOARD_KIND;
  sourcePageId: string;
  subtree: PuckTreeNode;
  copiedAt: number;
};

let memoryClipboard: BlockClipboardPayload | null = null;

function isBlockClipboardPayload(value: unknown): value is BlockClipboardPayload {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<BlockClipboardPayload>;
  return (
    v.version === BLOCK_CLIPBOARD_VERSION &&
    v.kind === BLOCK_CLIPBOARD_KIND &&
    typeof v.sourcePageId === "string" &&
    v.sourcePageId.length > 0 &&
    v.subtree != null &&
    typeof v.subtree === "object" &&
    typeof (v.subtree as PuckTreeNode).type === "string"
  );
}

/** Écrit le presse-papiers mémoire (+ OS en best-effort). */
export async function copyBlockToClipboard(
  payload: Omit<BlockClipboardPayload, "version" | "kind" | "copiedAt">,
): Promise<void> {
  const full: BlockClipboardPayload = {
    version: BLOCK_CLIPBOARD_VERSION,
    kind: BLOCK_CLIPBOARD_KIND,
    sourcePageId: payload.sourcePageId,
    subtree: structuredClone(payload.subtree),
    copiedAt: Date.now(),
  };
  memoryClipboard = full;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(JSON.stringify(full));
    }
  } catch {
    // Permissions / contexte non sécurisé : la mémoire module suffit.
  }
}

/** Lit le presse-papiers (mémoire d'abord, sinon OS). */
export async function readBlockClipboard(): Promise<BlockClipboardPayload | null> {
  if (memoryClipboard && isBlockClipboardPayload(memoryClipboard)) {
    return structuredClone(memoryClipboard);
  }
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.readText) {
      const text = await navigator.clipboard.readText();
      if (!text || !text.includes(BLOCK_CLIPBOARD_KIND)) return null;
      const parsed: unknown = JSON.parse(text);
      if (isBlockClipboardPayload(parsed)) {
        memoryClipboard = parsed;
        return structuredClone(parsed);
      }
    }
  } catch {
    // ignore
  }
  return null;
}

export function hasBlockClipboard(): boolean {
  return memoryClipboard != null;
}

/**
 * Focus dans un champ texte / TipTap → ne pas capturer Cmd/Ctrl+C/V/X.
 * Duck-typing (pas `instanceof HTMLElement`) pour rester correct cross-realm
 * quand l'événement vient du `document` de l'iframe Puck.
 */
export function isTextEditingTarget(target: EventTarget | null): boolean {
  if (target == null || typeof target !== "object") return false;
  const el = target as Partial<HTMLElement> & {
    nodeType?: number;
    tagName?: string;
    isContentEditable?: boolean;
    closest?: (selector: string) => Element | null;
  };
  if (el.nodeType !== 1 || typeof el.tagName !== "string") return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  if (
    typeof el.closest === "function" &&
    el.closest("[contenteditable='true'], .ProseMirror, [role='textbox']")
  ) {
    return true;
  }
  return false;
}
